import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Header, Table } from '../CommonComponents';
import { Container, Paper } from '../StyledComponents';

import { useDispatch, useSelector } from '../../redux/hooks';
import {
    fetchAllOrderRegistrations,
    selectAllOrderRegistrations,
    selectFetchingAllOrderRegistrations
} from '../../redux/actions/orderRegistrations';
import { setTableOptions, selectTableOptions } from '../../redux/actions/tables';
import { OrderRegistrationForListType, OrderRegistrationStatusType } from '../../types';
import { formatIpAddress } from '../../utils/parsing';

const statusLabel: Record<OrderRegistrationStatusType, string> = {
    pending: 'Väntande',
    processing: 'Behandlas',
    accepted: 'Accepterad',
    rejected: 'Nekad'
};

const statusFilterOptions = [
    { label: 'Väntande', value: 'pending' },
    { label: 'Behandlas', value: 'processing' },
    { label: 'Accepterad', value: 'accepted' },
    { label: 'Nekad', value: 'rejected' }
];

const columns = [{
    name: 'Namn',
    key: 'name',
    canSearch: true
}, {
    name: 'Skickad',
    key: 'createdAt',
    type: 'datetime' as const
}, {
    name: 'IP',
    key: 'ipAddress',
    getValue: ({ ipAddress }: { ipAddress?: string }) => formatIpAddress(ipAddress) || '-',
    canSearch: true
}, {
    name: 'Upphämtningsdatum',
    key: 'pickupDate',
    type: 'date' as const
}, {
    name: 'Lev-datum',
    key: 'deliveryDate',
    type: 'date' as const
}, {
    name: 'Mottagare',
    key: 'recipients',
    canSearch: true
}, {
    name: 'Status',
    key: 'status',
    getValue: ({ status }: { status: OrderRegistrationStatusType }) => statusLabel[status] || status,
    canSearch: true,
    canFilter: true,
    options: statusFilterOptions
}];

const TABLE_NAME = 'orderRegistrations';

export default function OrderRegistrations() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const registrations = useSelector(selectAllOrderRegistrations());
    const isFetching = useSelector(selectFetchingAllOrderRegistrations());
    const tableOptions = useSelector(selectTableOptions(TABLE_NAME));
    const didInitStatusFilter = React.useRef(false);

    React.useEffect(() => {
        dispatch(fetchAllOrderRegistrations());
    }, [dispatch]);

    // Default to pending once when table has no stored filters
    React.useEffect(() => {
        if (didInitStatusFilter.current) {
            return;
        }
        didInitStatusFilter.current = true;
        const filterValues = tableOptions?.filterValues || {};
        if (Object.keys(filterValues).length === 0) {
            dispatch(setTableOptions({
                name: TABLE_NAME,
                filterValues: { 'status-equals': 'pending' }
            }));
        }
    }, [dispatch, tableOptions?.filterValues]);

    const onRowClick = React.useCallback((row: OrderRegistrationForListType) => {
        if (row.status === 'accepted' && row.orderId) {
            navigate(`/orders/${row.orderId}`);
            return;
        }
        navigate(`/order-registrations/${row.id}`);
    }, [navigate]);

    return (
        <Container>
            <Header title="Från hemsida" />

            <Paper padding={0}>
                <Table
                    name={TABLE_NAME}
                    columns={columns}
                    data={registrations}
                    onRowClick={onRowClick}
                    emptyText={isFetching
                        ? 'Hämtar förfrågningar...'
                        : 'Det finns inga bokningsförfrågningar från hemsidan.'}
                    showPagination
                    defaultOrderBy="createdAt"
                    defaultOrderDirection="desc"
                />
            </Paper>
        </Container>
    );
}
